/**
 * @module CanvasResultsTable
 * @purpose Virtualised results table rendering decorated artefact rows.
 * @depends DecoratedArtefact (local types), react-i18next
 * @dependedOnBy CanvasResultsArea
 *
 * @rules
 * - Performance: parent handles loading/empty/error states; this component only renders rows
 * - State: none — pure display from props
 * - Errors: n/a
 */

import { useTranslation } from 'react-i18next'
import { Bookmark, Star, Tag } from 'lucide-react'
import type { DecoratedArtefact } from './types'
import { EventlogHostCell } from './EventlogHostCell'

const BYTES_PER_KB = 1024
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB
const BYTES_PER_GB = BYTES_PER_MB * BYTES_PER_KB
const EM_DASH = '—'

const SIZE_TYPES_WITH_BYTES = new Set(['mft', 'prefetch', 'file_inventory'])

interface SizeLabels {
  gb: string
  mb: string
  kb: string
  bytes: string
}

function formatSize(bytes: number, labels: SizeLabels): string {
  if (bytes >= BYTES_PER_GB) return `${(bytes / BYTES_PER_GB).toFixed(1)}${labels.gb}`
  if (bytes >= BYTES_PER_MB) return `${(bytes / BYTES_PER_MB).toFixed(1)}${labels.mb}`
  if (bytes >= BYTES_PER_KB) return `${(bytes / BYTES_PER_KB).toFixed(1)}${labels.kb}`
  return `${String(bytes)}${labels.bytes}`
}

function renderSize(artefact: DecoratedArtefact, labels: SizeLabels): string {
  if (artefact.sizeBytes === null) return EM_DASH
  if (!SIZE_TYPES_WITH_BYTES.has(artefact.artefactType)) return EM_DASH
  return formatSize(artefact.sizeBytes, labels)
}

function renderTimestamp(raw: string | null): string {
  if (!raw) return EM_DASH
  return new Date(raw).toLocaleString()
}

interface RowActionsProps {
  artefact: DecoratedArtefact
  onBookmark: (id: string) => void
  onStar: (id: string) => void
  onTag: (id: string) => void
}

function RowActions({ artefact, onBookmark, onStar, onTag }: RowActionsProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div className="flex gap-1">
      <button
        onClick={() => { onBookmark(artefact.artefactId) }}
        aria-label={t('canvas.table.bookmarkAction')}
        title={t('canvas.bookmarkTransient')}
        className="rounded p-1 transition-colors hover:bg-[var(--accent-dim)]"
        style={{ color: artefact.isBookmarked ? 'var(--accent)' : 'var(--text-muted)' }}
      >
        <Bookmark className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={() => { onStar(artefact.artefactId) }}
        aria-label={t('canvas.table.starAction')}
        title={t('canvas.bookmarkTransient')}
        className="rounded p-1 transition-colors hover:bg-[var(--accent-dim)]"
        style={{ color: artefact.isStarred ? 'var(--accent)' : 'var(--text-muted)' }}
      >
        <Star className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={() => { onTag(artefact.artefactId) }}
        aria-label={t('canvas.table.tagAction')}
        className="rounded p-1 transition-colors hover:bg-[var(--accent-dim)]"
        style={{ color: 'var(--text-muted)' }}
      >
        <Tag className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

interface CanvasResultsTableProps {
  artefacts: DecoratedArtefact[]
  selectedArtefactId: string | null
  onSelectArtefact: (id: string | null) => void
  onBookmark: (id: string) => void
  onStar: (id: string) => void
  onTag: (id: string) => void
}

/**
 * Artefact results table.
 *
 * @behaviour Renders rows from props; em-dash for null/inapplicable fields.
 *            Size shown only for mft, prefetch, file_inventory artefact types.
 * @errors n/a — parent renders loading/error/empty states
 */
export function CanvasResultsTable({
  artefacts,
  selectedArtefactId,
  onSelectArtefact,
  onBookmark,
  onStar,
  onTag,
}: CanvasResultsTableProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const sizeLabels: SizeLabels = {
    gb: t('canvas.sizes.gigabytes'),
    mb: t('canvas.sizes.megabytes'),
    kb: t('canvas.sizes.kilobytes'),
    bytes: t('canvas.sizes.bytes'),
  }

  const headers = [
    t('canvas.table.name'),
    t('canvas.table.type'),
    t('canvas.table.sourceEndpoint'),
    t('canvas.table.timestamp'),
    t('canvas.table.size'),
    t('canvas.table.actions'),
  ]

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            {headers.map((header) => (
              <th
                key={header}
                className="px-3 py-2 text-left text-xs font-[var(--font-mono)] tracking-wide uppercase"
                style={{ color: 'var(--text-muted)' }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {artefacts.map((artefact) => (
            <ResultsTableRow
              key={artefact.artefactId}
              artefact={artefact}
              isSelected={artefact.artefactId === selectedArtefactId}
              sizeLabels={sizeLabels}
              onSelectArtefact={onSelectArtefact}
              onBookmark={onBookmark}
              onStar={onStar}
              onTag={onTag}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface ResultsTableRowProps {
  artefact: DecoratedArtefact
  isSelected: boolean
  sizeLabels: SizeLabels
  onSelectArtefact: (id: string | null) => void
  onBookmark: (id: string) => void
  onStar: (id: string) => void
  onTag: (id: string) => void
}

function ResultsTableRow({
  artefact,
  isSelected,
  sizeLabels,
  onSelectArtefact,
  onBookmark,
  onStar,
  onTag,
}: ResultsTableRowProps): React.ReactElement {
  return (
    <tr
      onClick={() => { onSelectArtefact(isSelected ? null : artefact.artefactId) }}
      className="cursor-pointer transition-colors hover:bg-[var(--accent-dim)]"
      style={{
        background: isSelected ? 'var(--accent-dim)' : undefined,
        borderBottom: '1px solid var(--border)',
      }}
    >
      <td className="px-3 py-2 font-medium" style={{ color: 'var(--text)' }}>
        {artefact.displayName ?? EM_DASH}
      </td>
      <td className="px-3 py-2" style={{ color: 'var(--text-dim)' }}>
        {artefact.artefactType}
      </td>
      <td className="px-3 py-2 text-xs font-[var(--font-mono)]" style={{ color: 'var(--text-dim)' }}>
        {artefact.artefactType === 'eventlog'
          ? <EventlogHostCell artefact={artefact} />
          : artefact.endpointId}
      </td>
      <td className="px-3 py-2 text-xs font-[var(--font-mono)]" style={{ color: 'var(--text-dim)' }}>
        {renderTimestamp(artefact.timestampPrimary)}
      </td>
      <td className="px-3 py-2 text-xs font-[var(--font-mono)]" style={{ color: 'var(--text-dim)' }}>
        {renderSize(artefact, sizeLabels)}
      </td>
      <td
        className="px-3 py-2"
        onClick={(ev) => { ev.stopPropagation() }}
      >
        <RowActions artefact={artefact} onBookmark={onBookmark} onStar={onStar} onTag={onTag} />
      </td>
    </tr>
  )
}
